import type { AlpineComponent } from 'alpinejs'

import type { AvailableCountry, BrowserContextSuggestions } from '../../../../shopify'

const SESSION_KEY = 'geoIp'
const SET_GEOIP_SESSION_STORAGE = () => {
  const namespace = window.Shopify.shop
  window.sessionStorage.setItem(`${namespace}_${SESSION_KEY}`, String(true))
}

interface GeoIpData {
  geoIp: BrowserContextSuggestions | null
  showModal: boolean

  getSuggestions(): Promise<BrowserContextSuggestions>
  setGeoIpSession(): void

  readonly route: string
  readonly countryInfos: AvailableCountry | undefined
  readonly suggestion: BrowserContextSuggestions['suggestions'][0]['parts'] | undefined
  readonly text: string
  readonly geoIpSession: string | null
  readonly labelStayOnCurrentSite: string
  readonly labelGoToDetectedSite: string
}

function data(): AlpineComponent<GeoIpData> {
  return {
    geoIp: null,
    showModal: false,
    async init() {
      if (this.geoIpSession !== null) return
      try {
        this.geoIp = await this.getSuggestions()
        if (this.geoIp.detected_values.country.handle !== window.Shopify.country) {
          this.showModal = true
        }
      } catch (error) {
        console.error('Failed to fetch GeoIP suggestions:', error)
      }
      this.$watch('showModal', (val: boolean, oldVal: boolean) => {
        if (oldVal === true && val === false) this.setGeoIpSession()
      })
    },
    async getSuggestions() {
      const request = await fetch(this.route)
      if (!request.ok) throw new Error(`GeoIP request failed: ${request.status}`)
      return request.json()
    },
    setGeoIpSession() {
      SET_GEOIP_SESSION_STORAGE()
    },
    get text() {
      return window.translations.geo_ip_message_html
        .replace('[country_name]', this.countryInfos?.name ?? '')
        .replace('[current_country_name]', window.country)
        .replace('[currency_iso_code]', this.countryInfos?.currency.iso_code ?? '')
        .replace('[currency_symbol]', this.countryInfos?.currency.symbol ?? '')
    },
    get route() {
      return `${window.Shopify.routes.root}browsing_context_suggestions.json?country[enabled]=true&country[exclude]=${window.Shopify.country}&language[enabled]=true&language[exclude]=${window.Shopify.locale}`
    },
    get countryInfos() {
      return window.availableCountries.find((country) => {
        return country.iso_code === this.geoIp?.detected_values?.country.handle
      })
    },
    get suggestion() {
      return this.geoIp?.suggestions[0]?.parts
    },
    get geoIpSession() {
      const namespace = window.Shopify.shop
      return window.sessionStorage.getItem(`${namespace}_${SESSION_KEY}`)
    },
    get labelStayOnCurrentSite() {
      return window.translations.geo_ip_stay_on_current_site.replace('[current_country_name]', window.country)
    },
    get labelGoToDetectedSite() {
      return window.translations.geo_ip_go_to_detected_site.replace(
        '[detected_country_name]',
        this.geoIp?.detected_values?.country.name ?? ''
      )
    }
  }
}

export default {
  name: 'modalGeoIp',
  component: data
}
